/**
 * 客服 / 私信 入口 Composable
 *
 * @description
 * 对外提供统一的「打开客服弹框」「打开私信」「进入消息中心」能力。
 * 底层状态由 customerServiceStore + wukongIMStore 维护。
 *
 * 典型调用方：
 * - Footer 浮窗在线客服 → openService()
 * - Header 消息中心 → enterMessageCenter()
 * - 用户页「发消息」→ openPersonalChat({ targetUserId })
 */

import { ElMessage, ElLoading } from 'element-plus'
import { useCustomerServiceStore } from '~/stores/customerService'
import { useWuKongIMStore } from '~/stores/wukongIM'
import { useGlobalStore } from '~/stores/global'
import { ResponseCode } from '~/utils/constant'

export const useCustomerService = () => {
  const customerServiceStore = useCustomerServiceStore()
  const wukongIMStore = useWuKongIMStore()
  const globalStore = useGlobalStore()

  /**
   * 打开客服 Dialog（默认客服 Tab）
   * 在 /messages 页仅更新 store，不弹窗
   */
  const openPopup = (selectServiveId?: string | number) => {
    customerServiceStore.openPopup(selectServiveId)
  }

  /**
   * 打开私信 Dialog（默认私信 Tab）
   * @param options.sessionId 打开已有会话
   * @param options.targetUserId 向对方发起新会话
   */
  const openPersonalChat = (options?: { sessionId?: number; targetUserId?: number }) => {
    customerServiceStore.openPersonalPopup(options)
  }

  const closePopup = () => {
    customerServiceStore.closePopup()
  }

  const togglePopup = () => {
    customerServiceStore.togglePopup()
  }

  /**
   * 调用客服分配接口（consultation）
   * 打开弹框或进入消息中心前均需先分配客服，确保后端创建咨询会话
   *
   * @param serviceItem 可传 uid、serviceType 指定顾问
   * @param exhibitionId 展会场景可传展会 ID
   * @returns 是否分配成功
   */
  const assignCustomerService = async (
    serviceItem?: any,
    exhibitionId?: string | number
  ): Promise<boolean> => {
    const loading = ElLoading.service({
      lock: true,
      text: '分配客服中...',
      background: 'rgba(0, 0, 0, 0.1)',
    })
    setTimeout(() => {
      loading.close()
    }, 5000)

    try {
      const response = await useHttp.post('/api/customer-service/consultation/consultation', {
        accessContext: {
          clientPlatform: 'PC',
          clientBrowser: navigator.userAgent,
          clientOs: navigator.platform,
          clientUserAgent: navigator.userAgent,
          entryUrl: window.location.href,
          entryUrlTitle: document.title,
          exhibitionId: exhibitionId || '',
          referrerUrl: document.referrer || '',
        },
        customerId: globalStore.user?.id,
        exhibitionId: exhibitionId,
        serviceType: serviceItem?.serviceType || 'default',
        customerServiceUid: serviceItem?.uid || '',
      })
      loading.close()

      if (response.code === ResponseCode.SUCCESS && response.data) {
        return true
      }
      ElMessage.error(response.msg || '未能分配到客服，请稍后再试')
      return false
    } catch (error: any) {
      loading.close()
      console.error('在线咨询失败:', error)
      ElMessage.error(error.message || '连接客服失败')
      return false
    }
  }

  /**
   * 打开在线客服弹框（全站「在线客服」按钮）
   * 流程：分配客服 → openPopup
   */
  const openService = async (serviceItem?: any, exhibitionId?: string | number): Promise<boolean> => {
    const ok = await assignCustomerService(serviceItem, exhibitionId)
    if (ok) {
      customerServiceStore.openPopup(serviceItem?.uid || '')
    }
    return ok
  }

  /**
   * 进入消息中心页面 /messages
   * 流程：分配客服 → 设置客服 Tab 与选中参数 → 关闭 Dialog → 路由跳转
   * Header「消息中心」入口使用此方法
   */
  const enterMessageCenter = async (serviceItem?: any, exhibitionId?: string | number): Promise<boolean> => {
    const ok = await assignCustomerService(serviceItem, exhibitionId)
    if (!ok) return false

    customerServiceStore.setPopupTab('service')
    customerServiceStore.selectPersonalSessionId = null
    customerServiceStore.personalTargetUserId = null
    if (serviceItem?.uid) {
      customerServiceStore.selectServiveId = serviceItem.uid
    } else {
      customerServiceStore.selectServiveId = null
    }
    customerServiceStore.closePopup()

    const router = useRouter()
    await router.push('/messages')
    return true
  }

  /** 总未读（客服 + 私信） */
  const unreadCount = computed(() => wukongIMStore.totalUnread)
  const serviceUnread = computed(() => wukongIMStore.serviceUnread)
  const personalUnread = computed(() => wukongIMStore.personalUnread)

  const isVisible = computed(() => customerServiceStore.isPopupVisible)
  const serviceInfo = computed(() => customerServiceStore.customerServiceInfo)
  const conversationInfo = computed(() => customerServiceStore.conversationInfo)

  return {
    openPopup,
    openPersonalChat,
    closePopup,
    togglePopup,
    openService,
    enterMessageCenter,
    unreadCount,
    serviceUnread,
    personalUnread,
    isVisible,
    serviceInfo,
    conversationInfo,
  }
}
